import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from './UserContext';

// Create context
const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReadNotifications, setShowReadNotifications] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, initNotificationService } = useApi();
  const { isAuthenticated } = useUser();
  
  // Единственный эффект для инициализации сервиса уведомлений после аутентификации
  useEffect(() => {
    // Проверяем, что пользователь аутентифицирован и сервис ещё не инициализирован
    if (isAuthenticated && !isInitialized) {
      // console.log('Initializing notification service (one-time after auth)');
      
      // Асинхронная функция для инициализации
      const initializeNotifications = async () => {
        setLoading(true);
        try {
          // Запрашиваем только непрочитанные уведомления
          const data = await initNotificationService(false);
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
          // console.log('Notification service initialized successfully');
        } catch (err) {
          console.error('Failed to initialize notification service:', err);
          setError('Failed to initialize notification service');
        } finally {
          setLoading(false);
          // Устанавливаем флаг инициализации
          setIsInitialized(true);
        }
      };
      
      initializeNotifications();
    }
  }, [isAuthenticated, isInitialized, initNotificationService]);
  
  // Отметить уведомление как прочитанное
  const markAsRead = useCallback(async (notificationId) => {
    try {
      setError(null);
      
      const result = await markNotificationAsRead(notificationId);
      
      if (result.success) {
        if (showReadNotifications) {
          // Если показываем прочитанные, то просто обновляем их статус
          setNotifications(prev => 
            prev.map(notification => 
              notification._id === notificationId 
                ? { ...notification, read: true } 
                : notification
            )
          );
        } else {
          // Если не показываем прочитанные, удаляем из списка
          setNotifications(prev => 
            prev.filter(notification => notification._id !== notificationId)
          );
        }
        
        // Уменьшаем счетчик непрочитанных
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      return result;
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
      throw err;
    }
  }, [markNotificationAsRead, showReadNotifications]);
  
  // Отметить все уведомления как прочитанные
  const markAllAsRead = useCallback(async () => {
    try {
      setError(null);
      
      const result = await markAllNotificationsAsRead();
      
      if (result.success) {
        if (showReadNotifications) {
          // Если показываем прочитанные, обновляем статус всех
          setNotifications(prev => 
            prev.map(notification => ({ ...notification, read: true }))
          );
        } else {
          // Если не показываем прочитанные, очищаем список
          setNotifications([]);
        }
        
        // Сбрасываем счетчик непрочитанных
        setUnreadCount(0);
      }
      
      return result;
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read');
      throw err;
    }
  }, [markAllNotificationsAsRead, showReadNotifications]);
  
  // Обновление уведомлений (может быть вызвано вручную)
  const refreshNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getNotifications(showReadNotifications);
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch (err) {
      console.error('Error refreshing notifications:', err);
      setError('Failed to refresh notifications');
    } finally {
      setLoading(false);
    }
  }, [getNotifications, showReadNotifications]);
  
  // Переключение режима отображения прочитанных уведомлений
  const toggleShowReadNotifications = useCallback(() => {
    setShowReadNotifications(prev => !prev);
  }, []);
  
  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
    showReadNotifications,
    toggleShowReadNotifications
  };
  
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

// Custom hook to use the NotificationContext
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
} 